import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, query, where } from 'firebase/firestore'

import { db } from '@/firebase'
import useCreditValues from '@/hooks/useCreditValues'
import { formatCredits } from '@/hooks/useCreditValues'
import {
  CoreSeatIcon,
  AdvancedSeatIcon,
  UnlimitedSeatIcon,
  OrganizationTeacherSeatIcon,
  OrganizationStudentSeatIcon,
  GeneralFreeIcon,
  GeneralTeamIcon,
  GeneralOrganizationIcon,
} from '@/components/TeamSeatIcons'

// Team and organization product roles
const TEAM_ROLES = ['core_team_seat', 'advanced_team_seat', 'unlimited_team_seat']
const ORGANIZATION_ROLES = ['organization_teacher_seat', 'organization_student_seat']

const fetchPlanData = async () => {
  try {
    // Fetch team plans
    const teamQuery = query(collection(db, 'io_products'), where('role', 'in', TEAM_ROLES))
    const teamDocs = await getDocs(teamQuery)

    // Fetch organization plans
    const orgQuery = query(collection(db, 'io_products'), where('role', 'in', ORGANIZATION_ROLES))
    const orgDocs = await getDocs(orgQuery)

    const processPlans = async docs => {
      return Promise.all(
        docs.map(async doc => {
          const pricesCollectionRef = collection(db, 'io_products', doc.id, 'prices')
          const pricesSnapshot = await getDocs(pricesCollectionRef)
          const prices = pricesSnapshot.docs
            .map(priceDoc => ({
              ...priceDoc.data(),
              id: priceDoc.id,
            }))
            .filter(price => price.active)

          return {
            ...doc.data(),
            id: doc.id,
            prices,
          }
        }),
      )
    }

    const teamPlans = await processPlans(teamDocs.docs)
    const organizationPlans = await processPlans(orgDocs.docs)

    return { teamPlans, organizationPlans }
  } catch (error) {
    console.error('Error fetching plan data:', error)

    return { teamPlans: [], organizationPlans: [] }
  }
}

const formatPrice = (amount, currency = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
  }).format(amount / 100)
}

const getSeatIcon = (role, height = 24, width = 24) => {
  switch (role) {
    case 'core_team_seat':
      return <CoreSeatIcon height={height} width={width} />
    case 'advanced_team_seat':
      return <AdvancedSeatIcon height={height} width={width} />
    case 'unlimited_team_seat':
      return <UnlimitedSeatIcon height={height} width={width} />
    case 'organization_teacher_seat':
      return <OrganizationTeacherSeatIcon height={height} width={width} />
    case 'organization_student_seat':
      return <OrganizationStudentSeatIcon height={height} width={width} />
    default:
      return null
  }
}

const getSeatLabel = role => {
  switch (role) {
    case 'core_team_seat':
      return 'Core Seat'
    case 'advanced_team_seat':
      return 'Advanced Seat'
    case 'unlimited_team_seat':
      return 'Unlimited Seat'
    case 'organization_teacher_seat':
      return 'Teacher Seat'
    case 'organization_student_seat':
      return 'Student License'
    default:
      return 'Seat'
  }
}

const fallbackCredits = {
  core_team_seat: 1000,
  advanced_team_seat: 2500,
  unlimited_team_seat: 'Unlimited',
}

export const useDynamicPricingData = () => {
  const { data: creditValues = {}, isLoading: isLoadingCredits } = useCreditValues()

  const { data: planData, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['pricing-page-plans'],
    queryFn: fetchPlanData,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const pricingPlans = useMemo(() => {
    if (!planData || !creditValues) return []

    const plans = []

    // Individual/Free Plan (static for now)
    plans.push({
      id: 'individual',
      label: 'Free Plan',
      icon: <GeneralFreeIcon />,
      iconBg: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)',
      badge: null,
      description: 'Free for individual teachers',
      buttonLabel: 'Get Started',
      buttonSub: 'Includes 100 AI credits per month.',
      priceRows: [],
      features: [
        'Access to basic features',
        'LimitedAI feedback & grading',
        'Create & assign activities',
        'Google Classroom & Clever SSO & rostering syncing',
        'Basic chat and email support',
      ],
    })

    // Team Plan
    if (planData.teamPlans.length > 0) {
      const teamPriceRows = planData.teamPlans
        .map(plan => {
          const recurringPrice = plan.prices.find(p => p.type === 'recurring')
          const credits = plan?.stripe_metadata_credits || fallbackCredits[plan.role]

          return {
            label: getSeatLabel(plan.role),
            icon: getSeatIcon(plan.role, 24, 24) || <div />,
            iconColor: '#9B8AFB',
            price: recurringPrice ? formatPrice(recurringPrice.unit_amount) : '$0',
            unit: '/ seat',
            credits: credits ? formatCredits(credits) : 'Credits not available',
          }
        })
        .filter(row => row.price !== '$0') // Remove any invalid entries

      plans.push({
        id: 'team',
        label: 'Team plan',
        icon: <GeneralTeamIcon />,
        iconBg: 'linear-gradient(135deg, #9B8AFB 0%, #6B47DC 100%)',
        badge: 'Popular',
        description: 'Billed annually, per teacher seat.',
        buttonLabel: 'Get started',
        buttonSub: 'Quote generation & PO checkout available',
        priceRows: teamPriceRows,
        features: [
          'Student portfolios',
          'Teamspace for collaboration',
          'Google Classroom grade passback',
          'Purchase order checkout',
          'Access to success manager; 1 PD per year',
        ],
      })
    }

    // Organization Plan
    if (planData.organizationPlans.length > 0) {
      const orgPriceRows = planData.organizationPlans.map(plan => {
        const recurringPrice = plan.prices.find(p => p.type === 'recurring')
        const isStudentSeat = plan.role === 'organization_student_seat'

        return {
          label: getSeatLabel(plan.role),
          icon: getSeatIcon(plan.role, 24, 24) || <div />,
          iconColor: '#36BFFA',
          price: recurringPrice ? formatPrice(recurringPrice.unit_amount) : '$0',
          unit: '/ Year',
          sub: isStudentSeat ? 'Minimum of 150 Students' : undefined,
        }
      })

      plans.push({
        id: 'organization',
        label: 'Organization Plan',
        icon: <GeneralOrganizationIcon />,
        iconBg: 'linear-gradient(135deg, #36BFFA 0%, #1D8CF8 100%)',
        badge: null,
        description: 'Billed annually, per student license',
        buttonLabel: 'Talk to Sales',
        buttonSub: 'Quote generation & PO checkout available',
        priceRows: orgPriceRows,
        features: [
          'Create and manage unlimited teams',
          'Cohort and custom standards progress tracking',
          'Team workspaces for curriculum organization',
          'Unlimited AI grading, instant feedback, and portfolios',
          'Dedicated onboarding & success support',
          'LTI Integrations',
        ],
      })
    }

    return plans
  }, [planData, creditValues])

  return {
    pricingPlans,
    isLoading: isLoadingPlans || isLoadingCredits,
    teamPlans: planData?.teamPlans || [],
    organizationPlans: planData?.organizationPlans || [],
    creditValues,
  }
}
